import React from 'react'
import OportunidadesLayout from '../components/OportunidadesLayout'
import OportunidadesList from '../components/OportunidadesList'
import MonitoringList from '../components/MonitoringList'
import ClosedOpportunities from '../components/ClosedOpportunities'
import { OportunidadeModalProvider } from '../context/OportunidadeModalContext'
import { useLocation, useNavigate } from 'react-router-dom'
import OportunidadesStatsCards from '../components/OportunidadesStatsCards'
import { QuotaProvider, useQuota } from '../context/QuotaContext'
import OportunidadesReviewList from '../components/OportunidadesReviewList'
import { getReviewEnabled } from '@/modules/settings/services/reviewSettingsService'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { isMasterByEmail } from '@/shared/utils/role'
import { ReviewModalProvider, useReviewModal } from '../context/ReviewModalContext'
import ReviewOpportunityModal from '../components/ReviewOpportunityModal'
import { useFocusStore } from '../stores/focusStore'
import { useOportunidadesGapStats } from '../hooks/useOportunidadesGapStats'
import OportunidadesUpgradeHint from '../components/OportunidadesUpgradeHint'
import OportunidadesTrialBanner from '../components/OportunidadesTrialBanner'
import OportunidadesPostTrialBanner from '../components/OportunidadesPostTrialBanner'
import { useOportunidadesNoticeStore } from '../stores/oportunidadesNoticeStore'
import { OportunidadesNoticeModal } from '../components/OportunidadesNoticeModal'
import { useSignalsEnabled } from '@/modules/signals/hooks/useSignalsEnabled'
import { useTrialStatus } from '@/modules/plans/hooks/useTrialStatus'
import LazySection from '../components/LazySection'
import { Tabs } from '@/shared/ui'
import { useOportunidadesFiltersStore } from '../stores/oportunidadesFiltersStore'
import { ResultsDrawerProvider } from '@/modules/resultados'
import { CommunityDrawerProvider, useCommunityDrawer, useCommunityEnabled } from '@/modules/comunidade'
import { getOpportunityById } from '../services/oportunidadesService'

export default function OportunidadesPage() {
  const { user, profile } = useAuth()
  const isMaster = isMasterByEmail(user?.email) || profile?.role === 'master'
  const role: string | null = isMaster ? 'master' : (profile?.role ?? null)
  const [reviewEnabled, setReviewEnabled] = React.useState(false)
  // Nota: não usar useQuota aqui (fora do Provider)

  React.useEffect(() => {
    let mounted = true
    getReviewEnabled().then((on)=> { if (mounted) setReviewEnabled(on) }).catch(()=> {})
    return () => { mounted = false }
  }, [])

  return (
    <div className="w-full">
      <h1 className="mb-4 text-2xl font-semibold">Oportunidades</h1>
      <OportunidadesPostTrialBanner />
      <OportunidadesTrialBanner />
      <QuotaProvider>
        <OportunidadeModalProvider>
          <ResultsDrawerProvider>
            <CommunityDrawerProvider>
              <ReviewModalProvider>
                <InnerPage reviewEnabled={reviewEnabled} isMaster={!!isMaster} role={role} />
              </ReviewModalProvider>
            </CommunityDrawerProvider>
          </ResultsDrawerProvider>
        </OportunidadeModalProvider>
      </QuotaProvider>
    </div>
  )
}

function InnerPage({ reviewEnabled, isMaster, role }: { reviewEnabled: boolean; isMaster: boolean; role: string | null }) {
  const { open, close } = useReviewModal()
  const { exceeded, resolved } = useQuota()
  const location = useLocation()
  const navigate = useNavigate()
  const { openCommunityDrawer } = useCommunityDrawer()
  const communityEnabled = useCommunityEnabled()
  const hideStats = exceeded || !resolved
  const { stats: gapStats, loading: gapLoading, error: gapError } = useOportunidadesGapStats()
  const notice = useOportunidadesNoticeStore()
  const signalsEnabled = useSignalsEnabled()
  const { postTrialOnTrialPlan } = useTrialStatus()
  const focusId = useFocusStore((s) => s.focusId)
  const highlightIds = useFocusStore((s) => s.highlightIds)
  const hasFocusHighlight = !!focusId || (highlightIds?.length || 0) > 0
  const [activeTab, setActiveTab] = React.useState<'confirmed' | 'conversations' | 'in' | 'closed'>('confirmed')
  const inOutMode = useOportunidadesFiltersStore((s) => s.inOutMode)
  const setInOutMode = useOportunidadesFiltersStore((s) => s.setInOutMode)

  React.useEffect(() => {
    if (signalsEnabled !== true) return
    notice.loadStatus().catch(() => {})
  }, [signalsEnabled])

  React.useEffect(() => {
    if (activeTab !== 'conversations') return
    if (communityEnabled === true) return
    setActiveTab('confirmed')
  }, [activeTab, communityEnabled])

  // Ponto único: derivar foco da URL (?focus=:id ou legado ?opp=:id)
  React.useEffect(() => {
    try { useFocusStore.getState().fromURL(location.search) } catch {}
  }, [location.search])

  const lastMentionKeyRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    let cancelled = false

    async function run() {
      try {
        const params = new URLSearchParams(location.search || '')
        const oppRaw = params.get('focus') || params.get('opp')
        const msgRaw = params.get('community_message_id')
        if (!oppRaw || !msgRaw) return

        const oppId = Number(String(oppRaw).trim())
        const messageId = Number(String(msgRaw).trim())
        if (!Number.isFinite(oppId) || oppId <= 0) return
        if (!Number.isFinite(messageId) || messageId <= 0) return

        const key = `${oppId}:${messageId}`
        if (lastMentionKeyRef.current === key) return
        lastMentionKeyRef.current = key

        const row = await getOpportunityById(String(oppId))
        if (cancelled) return

        const symbol = row?.symbol ? String(row.symbol).toUpperCase() : String(oppId)
        openCommunityDrawer(
          {
            opportunityId: oppId,
            symbol,
            name: null,
            logoUrl: null,
          },
          { focusMessageId: messageId },
        )
      } catch {
        // ignore
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [location.search, openCommunityDrawer])

  // Ajusta filtro in/out conforme aba ativa
  React.useEffect(() => {
    if (activeTab === 'in') {
      if (inOutMode !== 'in') {
        setInOutMode('in')
      }
    } else if (inOutMode !== 'all') {
      setInOutMode('all')
    }
  }, [activeTab, inOutMode, setInOutMode])

  // Bridge para mensagens vindas do Service Worker (postMessage no notificationclick)
  React.useEffect(() => {
    function onMsg(ev: MessageEvent) {
      try {
        const d = (ev as any)?.data || {}
        if (d && d.type === 'open-opportunity' && d.oppId) {
          const id = String(d.oppId)
          try { useFocusStore.getState().setFocus(id) } catch {}
        }
        if (d && d.type === 'open-opportunities' && Array.isArray(d.oppIds)) {
          try { useFocusStore.getState().setHighlights(d.oppIds) } catch {}
        }
      } catch {}
    }
    try { window.addEventListener('message', onMsg) } catch {}
    return () => { try { window.removeEventListener('message', onMsg) } catch {} }
  }, [navigate])

  return (
    <>
      <OportunidadesNoticeModal
        open={signalsEnabled === true && notice.visible}
        html={notice.html}
        loading={false}
        error={notice.error}
        onConfirm={() => {
          notice.markAcknowledged().catch(() => {})
        }}
      />
      {!hideStats && <OportunidadesStatsCards />}
      <OportunidadesLayout>
        <div className="space-y-8">
          {isMaster && reviewEnabled && <OportunidadesReviewList />}

          <Tabs
            tabs={[
              { value: 'confirmed', label: 'Confirmadas' },
              ...(communityEnabled === true ? [{ value: 'conversations', label: 'Conversas' }] : []),
              { value: 'in', label: 'Estou dentro' },
              { value: 'closed', label: 'Encerradas' },
            ]}
            value={activeTab}
            onValueChange={(v) => {
              if (v === 'closed') {
                setActiveTab('closed')
              } else if (v === 'conversations') {
                setActiveTab('conversations')
              } else if (v === 'in') {
                setActiveTab('in')
              } else {
                setActiveTab('confirmed')
              }
            }}
          />

          {activeTab === 'confirmed' && (
            <div className="space-y-8">
              <OportunidadesList
                mode="confirmed"
                upgradeHint={
                  !gapLoading && !gapError ? (
                    <OportunidadesUpgradeHint
                      stats={gapStats}
                      role={role}
                      forceShow={postTrialOnTrialPlan}
                    />
                  ) : null
                }
              />
              <LazySection
                label="Carregando monitores..."
                forceVisible={hasFocusHighlight}
              >
                <MonitoringList />
              </LazySection>
            </div>
          )}

          {activeTab === 'in' && (
            <div className="space-y-8">
              <OportunidadesList
                mode="in"
                upgradeHint={
                  !gapLoading && !gapError ? (
                    <OportunidadesUpgradeHint
                      stats={gapStats}
                      role={role}
                      forceShow={postTrialOnTrialPlan}
                    />
                  ) : null
                }
              />
              <LazySection
                label="Carregando monitores..."
                forceVisible={hasFocusHighlight}
              >
                <MonitoringList />
              </LazySection>
            </div>
          )}

          {activeTab === 'conversations' && (
            <div className="space-y-8">
              <OportunidadesList mode="confirmed" conversationOnly />
            </div>
          )}

          {activeTab === 'closed' && (
            <div className="space-y-8">
              <LazySection
                label="Carregando oportunidades encerradas..."
                forceVisible={hasFocusHighlight}
              >
                <ClosedOpportunities />
              </LazySection>
            </div>
          )}
        </div>
      </OportunidadesLayout>
      <ReviewOpportunityModal open={open} onOpenChange={(v)=> v ? null : close()} onAfterPublish={()=> { /* no-op */ }} />
    </>
  )
}
